import { useEffect, useRef } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
// Override colori brand-aware (magenta), DOPO il css della libreria.
// Import relativo (non "@/") per coerenza col css della lib qui sopra.
import "../lib/cookieconsent-theme.css";
import {
  createCookieConsentConfig,
  type ConsentCategory,
} from "@/lib/cookieconsent-config";
import {
  enableAnalytics,
  disableAnalytics,
  enableMarketing,
  disableMarketing,
} from "@/lib/analytics";

function handleConsentCategories(acceptedCategories: ConsentCategory[]) {
  if (acceptedCategories.includes("analytics")) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }

  if (acceptedCategories.includes("marketing")) {
    enableMarketing();
  } else {
    disableMarketing();
  }
}

export function CookieBanner() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const config = createCookieConsentConfig({
      onConsentGiven: handleConsentCategories,
      onConsentChanged: handleConsentCategories,
    });

    CookieConsent.run(config);

    // Se l'utente ha gia dato il consenso in una sessione precedente,
    // attiva i servizi corrispondenti
    const currentCategories = CookieConsent.getUserPreferences().acceptedCategories as ConsentCategory[];
    if (currentCategories.length > 0) {
      handleConsentCategories(currentCategories);
    }
  }, []);

  // vanilla-cookieconsent supporta il dark mode tramite la classe
  // `.cc--darkmode` sul root `#cc-main`, ma non la applica da solo.
  // La sincronizziamo con il tema dell'app (classe `dark` su <html>),
  // tenendo conto delle pagine forzate in light (`force-light`).
  useEffect(() => {
    const root = document.documentElement;

    const syncTheme = () => {
      const isDark =
        root.classList.contains("dark") &&
        !root.classList.contains("force-light");
      const ccMain = document.getElementById("cc-main");
      ccMain?.classList.toggle("cc--darkmode", isDark);
      // Il banner compare anche ai crawler che eseguono JS: fuori dagli
      // snippet di Google, come l'age gate.
      ccMain?.setAttribute("data-nosnippet", "");
      return !!ccMain;
    };

    // #cc-main viene creato in modo asincrono da CookieConsent.run():
    // ritenta finche non esiste, poi continua a seguire i cambi di tema.
    if (!syncTheme()) {
      const bodyObserver = new MutationObserver(() => {
        if (syncTheme()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true });
    }

    const themeObserver = new MutationObserver(syncTheme);
    themeObserver.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => themeObserver.disconnect();
  }, []);

  return null;
}
